/**
 * Finance — Statutory & Management Reports:
 * Trial Balance · P&L · Balance Sheet · Cash Flow · AR/AP Aging · Project P&L · Payroll runs.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { PieChart, Scale, TrendingUp, Waves, Clock, Layers, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSearchParams } from "react-router-dom";
import { apiErrorMessage } from "@/lib/apiError";

const fmt = (n) => {
  try { return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0)); }
  catch { return `₹${Number(n || 0).toFixed(2)}`; }
};

export default function FinanceReports() {
  const { activeWorkspaceId } = useAuth();
  const [sp, setSp] = useSearchParams();
  const [tab, setTab] = useState(sp.get("tab") || "trial-balance");
  useEffect(() => { setSp((p) => { const n = new URLSearchParams(p); n.set("tab", tab); return n; }, { replace: true }); }, [tab, setSp]);
  const wsId = activeWorkspaceId;
  return (
    <div className="space-y-6" data-testid="finance-reports-page">
      <PageHeader title="Reports" subtitle="Statutory & management reports — computed live from your ledger." icon={PieChart} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="rounded-none flex-wrap h-auto bg-transparent p-0 border-b border-border w-full justify-start gap-1">
          <TabsTrigger value="trial-balance" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-tb"><Scale className="w-4 h-4 mr-2" />Trial Balance</TabsTrigger>
          <TabsTrigger value="pnl" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-pnl"><TrendingUp className="w-4 h-4 mr-2" />P&L</TabsTrigger>
          <TabsTrigger value="balance-sheet" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-bs"><Layers className="w-4 h-4 mr-2" />Balance Sheet</TabsTrigger>
          <TabsTrigger value="cash-flow" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-cf"><Waves className="w-4 h-4 mr-2" />Cash Flow</TabsTrigger>
          <TabsTrigger value="aging" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-aging"><Clock className="w-4 h-4 mr-2" />AR/AP Aging</TabsTrigger>
          <TabsTrigger value="project" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-project"><Users className="w-4 h-4 mr-2" />Project P&L</TabsTrigger>
          <TabsTrigger value="payroll" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-payroll"><Users className="w-4 h-4 mr-2" />Payroll</TabsTrigger>
          <TabsTrigger value="salary-cost" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-primary-foreground px-4 py-2" data-testid="rep-tab-salary-cost"><Users className="w-4 h-4 mr-2" />Salary Cost</TabsTrigger>
        </TabsList>
        <TabsContent value="trial-balance" className="mt-6"><TrialBalance wsId={wsId} /></TabsContent>
        <TabsContent value="pnl" className="mt-6"><PnL wsId={wsId} /></TabsContent>
        <TabsContent value="balance-sheet" className="mt-6"><BalanceSheet wsId={wsId} /></TabsContent>
        <TabsContent value="cash-flow" className="mt-6"><CashFlow wsId={wsId} /></TabsContent>
        <TabsContent value="aging" className="mt-6"><Aging wsId={wsId} /></TabsContent>
        <TabsContent value="project" className="mt-6"><ProjectPnL wsId={wsId} /></TabsContent>
        <TabsContent value="payroll" className="mt-6"><Payroll wsId={wsId} /></TabsContent>
        <TabsContent value="salary-cost" className="mt-6"><SalaryCostReport wsId={wsId} /></TabsContent>
      </Tabs>
    </div>
  );
}

/* Feb 2026 · Live salary-cost rollup pulled from HR compensation
 * revisions. Shares the same `/workspaces/{ws}/finance/salary-rollup`
 * endpoint as the HRCompensation page — single source of truth.
 * Gated to `hr.compensation.manage` on the backend so anyone reading
 * this tab already has clearance to see aggregate compensation. */
function SalaryCostReport({ wsId }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const load = () => {
    setLoading(true);
    setError(null);
    api.get(`/workspaces/${wsId}/finance/salary-rollup?as_of=${asOf}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.status === 403
        ? "Locked — needs `hr.compensation.manage` permission."
        : "Failed to load rollup"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (wsId) load(); }, [wsId, asOf]); // eslint-disable-line

  if (error) return <Card><CardContent className="p-6 text-sm text-muted-foreground text-center">{error}</CardContent></Card>;
  if (loading || !data) return <Card><CardContent className="p-6 text-muted-foreground text-sm">Loading…</CardContent></Card>;
  return (
    <div className="space-y-4" data-testid="salary-cost-report">
      <div className="flex items-end gap-3 flex-wrap">
        <div><Label>As of</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="h-10" data-testid="salary-cost-asof" /></div>
        <div className="text-xs text-muted-foreground pb-2">Employees covered: <strong>{data.headcount_covered}/{data.headcount_active}</strong>{data.headcount_missing_revision > 0 ? ` · ${data.headcount_missing_revision} missing a compensation revision` : ""}</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-widest">Employer cost / month</div><div className="text-lg font-bold font-mono" data-testid="salary-cost-employer">{fmt(data.total_employer_cost_monthly)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-widest">Net take-home / month</div><div className="text-lg font-bold font-mono">{fmt(data.total_net_monthly)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-widest">Annualised employer cost</div><div className="text-lg font-bold font-mono">{fmt(data.total_employer_cost_monthly * 12)}</div></CardContent></Card>
        <Card><CardContent className="p-3"><div className="text-[10px] uppercase text-muted-foreground tracking-widest">Avg per head</div><div className="text-lg font-bold font-mono">{fmt(data.headcount_covered ? data.total_employer_cost_monthly / data.headcount_covered : 0)}</div></CardContent></Card>
      </div>
      <Card><CardContent className="p-0">
        <div className="p-3 border-b border-border text-xs uppercase tracking-widest text-muted-foreground font-semibold">By department</div>
        {data.by_department.length === 0 ? (
          <div className="p-6 text-sm text-muted-foreground text-center">No compensation revisions on file yet. Add one on <a href="/app/hr/compensation" className="text-primary underline">Compensation</a>.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs uppercase text-muted-foreground">
              <tr><th className="text-left px-3 py-2">Department</th><th className="text-right px-3 py-2">Headcount</th><th className="text-right px-3 py-2">Monthly employer cost</th><th className="text-right px-3 py-2">% of total</th></tr>
            </thead>
            <tbody className="divide-y divide-border">
              {data.by_department.map((d) => (
                <tr key={d.department_id} data-testid={`salary-cost-dept-${d.department_id}`}>
                  <td className="px-3 py-2">{d.department_name}</td>
                  <td className="text-right px-3 py-2 font-mono">{d.headcount}</td>
                  <td className="text-right px-3 py-2 font-mono">{fmt(d.employer_cost_monthly)}</td>
                  <td className="text-right px-3 py-2 font-mono">{data.total_employer_cost_monthly ? `${((d.employer_cost_monthly / data.total_employer_cost_monthly) * 100).toFixed(1)}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </CardContent></Card>
      <PayrollBudgetVsActual wsId={wsId} />
    </div>
  );
}


function PayrollBudgetVsActual({ wsId }) {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [monthlyInput, setMonthlyInput] = useState("");

  const load = () => {
    api.get(`/workspaces/${wsId}/finance/payroll-budget-actual?year=${year}`)
      .then((r) => { setData(r.data); setMonthlyInput(String(r.data.monthly_budget || "")); })
      .catch(() => setData(null));
  };
  useEffect(() => { if (wsId) load(); }, [wsId, year]); // eslint-disable-line

  const saveBudget = async () => {
    const val = Number(monthlyInput);
    if (!(val >= 0)) return;
    setSaving(true);
    try {
      await api.put(`/workspaces/${wsId}/finance/payroll-budget`,
        { year, monthly_budget: val });
      load();
    } finally { setSaving(false); }
  };

  if (!data) return null;
  const maxActual = Math.max(1, ...data.months.map((m) => m.actual));
  const budgetLinePct = data.monthly_budget ? (data.monthly_budget / maxActual) * 100 : 0;
  return (
    <Card data-testid="payroll-budget-widget"><CardContent className="p-4 space-y-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Payroll budget vs actual</div>
          <div className="text-xs text-muted-foreground">Overlay actual monthly employer cost against your target so over-runs jump out early.</div>
        </div>
        <div className="flex items-center gap-2">
          <Label className="text-xs" htmlFor="pb-year">Year</Label>
          <Input id="pb-year" type="number" value={year} onChange={(e) => setYear(Number(e.target.value) || year)} className="h-9 w-24" data-testid="pb-year-input" />
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Label className="text-xs">Monthly budget (₹)</Label>
        <Input type="number" value={monthlyInput} onChange={(e) => setMonthlyInput(e.target.value)} className="h-9 w-40 font-mono" data-testid="pb-monthly-input" />
        <Button size="sm" onClick={saveBudget} disabled={saving} data-testid="pb-save">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : "Save target"}
        </Button>
        <div className="text-xs text-muted-foreground">Annual target: <strong>{fmt(data.annual_budget)}</strong> · YTD actual: <strong>{fmt(data.total_actual)}</strong></div>
      </div>
      <div className="grid grid-cols-6 sm:grid-cols-12 gap-1 mt-2" data-testid="pb-months">
        {data.months.map((m) => {
          const h = Math.max(4, (m.actual / maxActual) * 100);
          const over = m.delta > 0;
          return (
            <div key={m.month} className="flex flex-col items-center" data-testid={`pb-month-${m.month}`} title={`${m.month} · actual ${fmt(m.actual)}${data.monthly_budget ? ` · Δ ${m.delta > 0 ? "+" : ""}${fmt(m.delta)} (${m.pct}%)` : ""}`}>
              <div className="w-full h-24 bg-muted/40 relative flex items-end">
                {data.monthly_budget > 0 && (
                  <div className="absolute left-0 right-0 border-t-2 border-dashed border-primary/50" style={{ bottom: `${budgetLinePct}%` }} />
                )}
                <div className={`w-full ${over ? "bg-red-500" : "bg-emerald-500"}`} style={{ height: `${h}%` }} />
              </div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono">{m.month.slice(5)}</div>
            </div>
          );
        })}
      </div>
      <div className="text-[10px] text-muted-foreground">Red bars = actual over budget · dashed line = monthly target</div>
    </CardContent></Card>
  );
}

function TrialBalance({ wsId }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const load = () => api.get(`/finance/workspaces/${wsId}/reports/trial-balance?as_of=${asOf}`).then((r) => setData(r.data));
  useEffect(() => { if (wsId) load(); }, [wsId, asOf]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><Label>As of</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="tb-asof-input" /></div>
        {data && <Badge variant={data.totals.balanced ? "default" : "destructive"} data-testid="tb-balanced-badge">{data.totals.balanced ? "✓ Balanced" : "⚠ Unbalanced"}</Badge>}
      </div>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr className="text-left"><th className="p-3">Code</th><th className="p-3">Name</th><th className="p-3">Type</th><th className="p-3 text-right">Debit</th><th className="p-3 text-right">Credit</th></tr></thead>
          <tbody>
            {data?.rows?.map((r) => (
              <tr key={r.account_id} className="border-t" data-testid={`tb-row-${r.code}`}>
                <td className="p-3 font-mono">{r.code}</td>
                <td className="p-3">{r.name}</td>
                <td className="p-3"><Badge variant="outline" className="text-xs">{r.type}</Badge></td>
                <td className="p-3 text-right font-mono">{r.debit ? fmt(r.debit) : "—"}</td>
                <td className="p-3 text-right font-mono">{r.credit ? fmt(r.credit) : "—"}</td>
              </tr>
            ))}
          </tbody>
          {data && <tfoot className="border-t bg-muted/20 font-mono"><tr><td className="p-3" colSpan={3}>Totals</td><td className="p-3 text-right" data-testid="tb-total-dr">{fmt(data.totals.debit)}</td><td className="p-3 text-right" data-testid="tb-total-cr">{fmt(data.totals.credit)}</td></tr></tfoot>}
        </table>
      </CardContent></Card>
    </div>
  );
}

function PnL({ wsId }) {
  // Default: fiscal-year-start (Apr 1 for Indian FY) → today
  const fyStart = () => {
    const now = new Date();
    const y = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
    return `${y}-04-01`;
  };
  const [start, setStart] = useState(fyStart());
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const load = () => api.get(`/finance/workspaces/${wsId}/reports/pnl?start=${start}&end=${end}`).then((r) => setData(r.data)).catch(() => toast.error("Failed"));
  useEffect(() => { if (wsId) load(); }, [wsId, start, end]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="pnl-start-input" /></div>
        <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="pnl-end-input" /></div>
      </div>
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Card><CardContent className="p-6">
            <h3 className="font-semibold mb-3 text-emerald-600">Revenue</h3>
            <table className="w-full text-sm">
              <tbody>{data.revenue.lines.map((r) => (
                <tr key={r.code} className="border-t"><td className="p-2 font-mono text-xs">{r.code}</td><td className="p-2">{r.name}</td><td className="p-2 text-right font-mono">{fmt(r.amount)}</td></tr>
              ))}</tbody>
              <tfoot className="border-t font-mono font-semibold"><tr><td colSpan={2} className="p-2">Total</td><td className="p-2 text-right" data-testid="pnl-rev-total">{fmt(data.revenue.total)}</td></tr></tfoot>
            </table>
          </CardContent></Card>
          <Card><CardContent className="p-6">
            <h3 className="font-semibold mb-3 text-rose-600">Expenses</h3>
            <table className="w-full text-sm">
              <tbody>{data.expenses.lines.map((r) => (
                <tr key={r.code} className="border-t"><td className="p-2 font-mono text-xs">{r.code}</td><td className="p-2">{r.name}</td><td className="p-2 text-right font-mono">{fmt(r.amount)}</td></tr>
              ))}</tbody>
              <tfoot className="border-t font-mono font-semibold"><tr><td colSpan={2} className="p-2">Total</td><td className="p-2 text-right" data-testid="pnl-exp-total">{fmt(data.expenses.total)}</td></tr></tfoot>
            </table>
          </CardContent></Card>
          <Card className="lg:col-span-2 border-primary/40"><CardContent className="p-6 flex items-center justify-between">
            <div>
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Net profit</div>
              <div className={`text-3xl font-mono font-semibold mt-1 ${data.net_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="pnl-net">{fmt(data.net_profit)}</div>
            </div>
            {data.margin_pct != null && (
              <div className="text-right"><div className="text-xs text-muted-foreground uppercase tracking-wide">Margin</div><div className="text-2xl font-mono">{data.margin_pct}%</div></div>
            )}
          </CardContent></Card>
        </div>
      )}
    </div>
  );
}

function BalanceSheet({ wsId }) {
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/reports/balance-sheet?as_of=${asOf}`).then((r) => setData(r.data));
  }, [wsId, asOf]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><Label>As of</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="bs-asof-input" /></div>
        {data && <Badge variant={data.balanced ? "default" : "destructive"} data-testid="bs-balanced-badge">{data.balanced ? "✓ A = L + E" : `⚠ Diff ${fmt(data.difference)}`}</Badge>}
      </div>
      {data && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <BsSection title="Assets" total={data.assets.total} lines={data.assets.lines} tone="emerald" testid="bs-assets" />
          <BsSection title="Liabilities" total={data.liabilities.total} lines={data.liabilities.lines} tone="amber" testid="bs-liab" />
          <BsSection title="Equity" total={data.equity.total} lines={data.equity.lines} tone="blue" testid="bs-equity" />
        </div>
      )}
    </div>
  );
}

function BsSection({ title, total, lines, tone, testid }) {
  const tones = { emerald: "text-emerald-600", amber: "text-amber-600", blue: "text-blue-600" };
  return (
    <Card><CardContent className="p-6">
      <h3 className={`font-semibold mb-3 ${tones[tone]}`}>{title}</h3>
      <table className="w-full text-sm">
        <tbody>{lines.map((l, i) => (
          <tr key={i} className="border-t"><td className="p-2 font-mono text-xs">{l.code}</td><td className="p-2">{l.name}</td><td className="p-2 text-right font-mono">{fmt(l.amount)}</td></tr>
        ))}</tbody>
        <tfoot className="border-t font-mono font-semibold"><tr><td colSpan={2} className="p-2">Total</td><td className="p-2 text-right" data-testid={`${testid}-total`}>{fmt(total)}</td></tr></tfoot>
      </table>
    </CardContent></Card>
  );
}

function CashFlow({ wsId }) {
  const [start, setStart] = useState(new Date(new Date().setDate(1)).toISOString().slice(0, 10));
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/reports/cash-flow?start=${start}&end=${end}`).then((r) => setData(r.data));
  }, [wsId, start, end]);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="cf-start-input" /></div>
        <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="cf-end-input" /></div>
      </div>
      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Kpi label="Opening cash" value={fmt(data.opening)} tone="blue" testid="cf-opening" />
            <Kpi label="Inflows" value={fmt(data.total_inflow)} tone="emerald" testid="cf-in" />
            <Kpi label="Outflows" value={fmt(data.total_outflow)} tone="rose" testid="cf-out" />
            <Kpi label="Closing cash" value={fmt(data.closing)} tone="blue" testid="cf-closing" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card><CardContent className="p-6"><h3 className="font-semibold text-emerald-600 mb-3">Inflows</h3>
              <div className="space-y-2 max-h-[300px] overflow-auto text-sm">{data.inflows.map((i, k) => (
                <div key={k} className="flex justify-between border-b py-1"><span>{i.date} — {i.memo || i.reference}</span><span className="font-mono">{fmt(i.amount)}</span></div>
              ))}{data.inflows.length === 0 && <p className="text-xs text-muted-foreground">No inflows.</p>}</div>
            </CardContent></Card>
            <Card><CardContent className="p-6"><h3 className="font-semibold text-rose-600 mb-3">Outflows</h3>
              <div className="space-y-2 max-h-[300px] overflow-auto text-sm">{data.outflows.map((i, k) => (
                <div key={k} className="flex justify-between border-b py-1"><span>{i.date} — {i.memo || i.reference}</span><span className="font-mono">{fmt(i.amount)}</span></div>
              ))}{data.outflows.length === 0 && <p className="text-xs text-muted-foreground">No outflows.</p>}</div>
            </CardContent></Card>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ label, value, tone, testid }) {
  const t = { emerald: "text-emerald-600 border-emerald-500/30", rose: "text-rose-600 border-rose-500/30", blue: "text-blue-600 border-blue-500/30" };
  return (
    <Card className={`border ${t[tone]}`} data-testid={testid}>
      <CardContent className="p-4"><div className="text-xs text-muted-foreground uppercase">{label}</div><div className={`text-xl font-mono font-semibold mt-1 ${t[tone]}`}>{value}</div></CardContent>
    </Card>
  );
}

function Aging({ wsId }) {
  const [ar, setAr] = useState(null);
  const [ap, setAp] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/finance/workspaces/${wsId}/reports/ar-aging`).then((r) => setAr(r.data));
    api.get(`/finance/workspaces/${wsId}/reports/ap-aging`).then((r) => setAp(r.data));
  }, [wsId]);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <AgingCard title="AR (customer receivables)" data={ar} rowsKey="invoices" nameKey="client_name" numKey="doc_no" testid="ar" />
      <AgingCard title="AP (vendor payables)" data={ap} rowsKey="bills" nameKey="vendor_name" numKey="bill_no" testid="ap" />
    </div>
  );
}

function AgingCard({ title, data, rowsKey, nameKey, numKey, testid }) {
  if (!data) return <Card><CardContent className="p-6 text-sm text-muted-foreground">Loading…</CardContent></Card>;
  return (
    <Card><CardContent className="p-6 space-y-4" data-testid={`aging-${testid}`}>
      <h3 className="font-semibold">{title}</h3>
      <div className="grid grid-cols-4 gap-2">
        {Object.entries(data.buckets).map(([k, v]) => (
          <div key={k} className="text-center p-2 rounded border" data-testid={`aging-${testid}-${k}`}>
            <div className="text-xs text-muted-foreground">{k}d</div>
            <div className="font-mono font-semibold">{fmt(v)}</div>
          </div>
        ))}
      </div>
      <div className="text-sm font-mono border-t pt-2 flex justify-between"><span>Total outstanding</span><span data-testid={`aging-${testid}-total`}>{fmt(data.total_outstanding)}</span></div>
      <table className="w-full text-xs mt-2">
        <thead className="bg-muted/40"><tr className="text-left"><th className="p-2">Doc</th><th className="p-2">Party</th><th className="p-2 text-right">Outstanding</th><th className="p-2">Days</th></tr></thead>
        <tbody>{(data[rowsKey] || []).map((r, i) => (
          <tr key={i} className="border-t"><td className="p-2 font-mono">{r[numKey]}</td><td className="p-2">{r[nameKey]}</td><td className="p-2 text-right font-mono">{fmt(r.outstanding)}</td><td className="p-2">{r.days_past_due}</td></tr>
        ))}</tbody>
      </table>
    </CardContent></Card>
  );
}

function ProjectPnL({ wsId }) {
  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState("");
  const [start, setStart] = useState("2026-01-01");
  const [end, setEnd] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/projects`).then((r) => setProjects(Array.isArray(r.data) ? r.data : (r.data.projects || [])));
  }, [wsId]);
  const run = async () => {
    if (!projectId) return;
    try {
      const { data } = await api.get(`/finance/workspaces/${wsId}/reports/project-pnl?project_id=${projectId}&start=${start}&end=${end}`);
      setData(data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3 flex-wrap">
        <div className="w-64"><Label>Project</Label>
          <Select value={projectId} onValueChange={setProjectId}>
            <SelectTrigger data-testid="ppnl-project-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
            <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>From</Label><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="ppnl-start-input" /></div>
        <div><Label>To</Label><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="ppnl-end-input" /></div>
        <Button onClick={run} disabled={!projectId} data-testid="ppnl-run-btn">Run</Button>
      </div>
      {data && (
        <Card><CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Kpi label="Revenue" value={fmt(data.revenue.total)} tone="emerald" testid="ppnl-rev" />
            <Kpi label="Expenses" value={fmt(data.expenses.total)} tone="rose" testid="ppnl-exp" />
            <Kpi label="Net profit" value={fmt(data.net_profit)} tone="blue" testid="ppnl-net" />
          </div>
          {data.margin_pct != null && <div className="text-sm">Margin: <span className="font-mono font-semibold">{data.margin_pct}%</span></div>}
        </CardContent></Card>
      )}
    </div>
  );
}

function Payroll({ wsId }) {
  const [runs, setRuns] = useState([]);
  const [accts, setAccts] = useState([]);
  const [form, setForm] = useState({ period: new Date().toISOString().slice(0, 7), payment_account_id: "", tds_rate_default: 10 });
  const load = () => api.get(`/finance/workspaces/${wsId}/payroll-runs`).then((r) => setRuns(r.data.runs || []));
  useEffect(() => {
    if (!wsId) return;
    load();
    api.get(`/finance/workspaces/${wsId}/accounts`).then((r) => setAccts(r.data.accounts || []));
  }, [wsId]);
  const payAccts = accts.filter((a) => ["bank", "cash"].includes(a.subtype));
  const run = async () => {
    try {
      const p = { ...form, tds_rate_default: Number(form.tds_rate_default) };
      await api.post(`/finance/workspaces/${wsId}/payroll-runs`, p);
      toast.success("Payroll posted"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
  };
  return (
    <div className="space-y-4">
      <Card><CardContent className="p-6 space-y-3">
        <h3 className="font-semibold">Run new payroll batch</h3>
        <div className="grid grid-cols-4 gap-3 items-end">
          <div><Label>Period (YYYY-MM)</Label><Input value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="payroll-period-input" /></div>
          <div><Label>Pay from account</Label>
            <Select value={form.payment_account_id} onValueChange={(v) => setForm({ ...form, payment_account_id: v })}>
              <SelectTrigger data-testid="payroll-account-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
              <SelectContent>{payAccts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} — {a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Default TDS %</Label><Input type="number" step="0.01" value={form.tds_rate_default} onChange={(e) => setForm({ ...form, tds_rate_default: e.target.value })} data-testid="payroll-tds-input" /></div>
          <Button onClick={run} disabled={!form.payment_account_id || !form.period} data-testid="payroll-run-btn">Run payroll</Button>
        </div>
      </CardContent></Card>
      <Card><CardContent className="p-0">
        <table className="w-full text-sm">
          <thead className="bg-muted/40"><tr className="text-left"><th className="p-3">Period</th><th className="p-3 text-right">Employees</th><th className="p-3 text-right">Gross</th><th className="p-3 text-right">TDS</th><th className="p-3 text-right">Net</th><th className="p-3">Status</th><th className="p-3">Payslips</th></tr></thead>
          <tbody>
            {runs.map((r) => {
              const token = typeof window !== "undefined" ? (localStorage.getItem("co_token") || "") : "";
              const base = process.env.REACT_APP_BACKEND_URL || "";
              const zipUrl = `${base}/api/v1/payroll-runs/${r.id}/payslips.zip?auth=${encodeURIComponent(token)}`;
              return (
                <tr key={r.id} className="border-t" data-testid={`payroll-row-${r.period}`}>
                  <td className="p-3 font-mono">{r.period}</td>
                  <td className="p-3 text-right">{r.employee_count}</td>
                  <td className="p-3 text-right font-mono">{fmt(r.total_gross)}</td>
                  <td className="p-3 text-right font-mono">{fmt(r.total_tds)}</td>
                  <td className="p-3 text-right font-mono">{fmt(r.total_net)}</td>
                  <td className="p-3"><Badge variant={r.status === "voided" ? "destructive" : "outline"}>{r.status}</Badge></td>
                  <td className="p-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <a href={zipUrl} target="_blank" rel="noopener noreferrer" download className="text-xs text-primary hover:underline" data-testid={`payroll-zip-${r.period}`}>ZIP all</a>
                      <button type="button"
                        onClick={async () => {
                          if (!window.confirm(`Email payslips to all ${(r.breakdown || []).length} employees in ${r.period}? This uses real SMTP.`)) return;
                          try {
                            const { data } = await api.post(`/payroll-runs/${r.id}/email-all-payslips`);
                            toast.success(`Sent ${data.sent} payslip${data.sent === 1 ? "" : "s"}${data.failed ? ` · ${data.failed} failed` : ""}`);
                          } catch (e) { toast.error("Bulk email failed"); }
                        }}
                        className="text-xs text-primary hover:underline"
                        data-testid={`payroll-email-all-${r.period}`}
                        disabled={r.status === "voided"}
                      >Email all</button>
                      {r.status !== "voided" && (
                        <button type="button"
                          onClick={async () => {
                            const reason = window.prompt(`Void payroll for ${r.period}? This reverses the journal entry.\n\nReason:`, "");
                            if (reason === null) return;
                            try {
                              await api.post(`/finance/payroll-runs/${r.id}/void`, null, { params: { reason: reason || "manual reversal" } });
                              toast.success(`Payroll ${r.period} voided`);
                              load();
                            } catch (e) { toast.error("Void failed"); }
                          }}
                          className="text-xs text-destructive hover:underline"
                          data-testid={`payroll-void-${r.period}`}
                        >Void</button>
                      )}
                      <details className="inline-block">
                        <summary className="text-xs text-muted-foreground hover:text-primary cursor-pointer">Per employee ({(r.breakdown || []).length})</summary>
                        <div className="absolute z-10 bg-background border border-border shadow-lg p-2 mt-1 max-h-64 overflow-y-auto min-w-[240px]">
                          {(r.breakdown || []).map((bd) => (
                            <div key={bd.employee_id} className="flex items-center gap-2 text-[11px] py-1 px-2 hover:bg-muted">
                              <a href={`${base}/api/v1/payroll-runs/${r.id}/employees/${bd.employee_id}/payslip?auth=${encodeURIComponent(token)}`}
                                 target="_blank" rel="noopener noreferrer"
                                 className="flex-1 truncate text-primary hover:underline"
                                 title={bd.name}>{bd.name}</a>
                              <button type="button"
                                onClick={async () => {
                                  try {
                                    await api.post(`/payroll-runs/${r.id}/employees/${bd.employee_id}/email-payslip`, {});
                                    toast.success(`Sent to ${bd.name}`);
                                  } catch (e) { toast.error("Email failed"); }
                                }}
                                className="text-[10px] text-muted-foreground hover:text-primary"
                                title="Email this payslip"
                              >✉</button>
                            </div>
                          ))}
                        </div>
                      </details>
                    </div>
                  </td>
                </tr>
              );
            })}
            {runs.length === 0 && <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No payroll runs yet.</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>
    </div>
  );
}
